import * as tslib_1 from "tslib";
import React from 'react';
import isEqual from 'react-fast-compare';
import { Url, URLS } from '@wix/da-url';
import ddt from '@wix/da-ddt';
import Notifications from '../Notifications';
import ErrorBoundary from '../ErrorBoundary';
import Modals from '../Modals';
import { initPopups as initSisuPopups } from '../SisuIframePopup';
import BackToTop from '../BackToTop';
import CommonHelmet from '../Helmet';
import Head from './Head';
import { MatureContext, SessionContext } from '../Context';
import { MobileContext } from '../Context/MobileContext';
import { ThemeContext } from '../Context/ThemeContext';
import FlagSwitcher from '../redux/flags/FlagSwitcher';
import { FirstTimeFlag } from '../redux/firstTimeFlags';
import EclipseWelcomePopup from '../Popup/Eclipse/Welcome';
import VerificationPopup from '../VerificationPopup';
import DuperbrowsePageWrapper from '../Duperbrowse/DuperbrowsePageWrapper';
import { BrowserFeatureContext } from '../Context/BrowserFeatureContext';
import EditorBaseTheme from '../DaEditor/components/BaseTheme';
import { ScreenSizeContextProvider } from '../Measured';
import { BiLoggerContextProvider } from '../biLogger';
/**
 * This is designed to wrap your render() output in App.tsx
 * it provides the common components that we need for some
 * shared things.
 *
 * Things it includes:
 *
 * - <Notifications />
 * - <Modals />
 *
 * The dream
 * This component wraps all the header/context etc but currently
 * there is some variation between projects, so for now its limited
 * in its scope.
 */
var DaApp = /** @class */ (function (_super) {
    tslib_1.__extends(DaApp, _super);
    function DaApp() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.renderSSRPopups = function () {
            var _a = _this.props, isLoggedIn = _a.isLoggedIn, firstTimeFlags = _a.firstTimeFlags, hasJoinedWithEclipse = _a.hasJoinedWithEclipse, isMobile = _a.isMobile, publicSession = _a.publicSession;
            if (isLoggedIn && firstTimeFlags[FirstTimeFlag.WelcomePopupNotClosed]) {
                return !publicSession.isVerified && hasJoinedWithEclipse ? (React.createElement(VerificationPopup, { dropFlagOnClose: true })) : (!isMobile && (React.createElement(EclipseWelcomePopup, null)) /* we only have a desktop version of this popup */);
            }
        };
        _this.onMessage = function (event) {
            // Only handle events that must be handled in all apps
            var _a = _this.props, pushBadgeModal = _a.pushBadgeModal, isMobile = _a.isMobile;
            var expectedOrigin = Url.origin();
            if (event.origin !== expectedOrigin) {
                return;
            }
            var data;
            try {
                data = JSON.parse(event.data);
                if (!data.type) {
                    return;
                }
            }
            catch (e) {
                return;
            }
            var isInsideIframe;
            try {
                isInsideIframe = window.self !== window.top;
            }
            catch (e) {
                isInsideIframe = true;
            }
            ddt.log('shared', 'got MessageEvent: ', event);
            switch (data.type) {
                case 'NotificationsDropdown':
                    var notificationsIframe = document.getElementById('eclipse-notifications-iframe');
                    // only resize the notifications iframe on desktop
                    if (notificationsIframe && data.height && !isMobile) {
                        notificationsIframe.style.height = data.height;
                    }
                    break;
                case 'OpenBadgeModal':
                    // badge modal could be fired from the recent messages iframe in header
                    if (isInsideIframe) {
                        return;
                    }
                    if (data.badgeName && data.userId) {
                        ddt.log('shared', 'firing pushBadgeModal', data);
                        pushBadgeModal(data.badgeName, data.userId, data.title, data.onBadgeSent);
                    }
                    break;
                case 'GiveModal':
                    if (data.command === 'badge_receipt') {
                        // Relay the message to the notifications iframe too
                        var iframe = document.getElementById('eclipse-notifications-iframe');
                        if (iframe && iframe.contentWindow) {
                            // Notifications iframe is always hosted on www.deviantart.com
                            // so target origin is URLS.ROOT
                            iframe.contentWindow.postMessage(event.data, URLS.ROOT);
                        }
                    }
                    break;
                default:
            }
        };
        return _this;
    }
    DaApp.prototype.componentDidMount = function () {
        if (typeof window !== 'undefined') {
            window.addEventListener('message', this.onMessage);
            initSisuPopups({
                pushModal: this.props.pushModal,
                pushSisuSocialModal: this.props.pushSisuSocialModal,
            });
        }
    };
    DaApp.prototype.componentWillUnmount = function () {
        if (typeof window !== 'undefined') {
            window.removeEventListener('message', this.onMessage);
        }
    };
    DaApp.prototype.shouldComponentUpdate = function (nextProps) {
        return !isEqual(this.props, nextProps);
    };
    DaApp.prototype.render = function () {
        var _a = this.props, modalsRenderFunc = _a.modalsRenderFunc, _b = _a.showMatureContent, showMatureContent = _b === void 0 ? true : _b, _c = _a.isMobile, isMobile = _c === void 0 ? false : _c, theme = _a.theme, headTags = _a.headTags, backToTop = _a.backToTop, _d = _a.isDuperbrowseEnabled, isDuperbrowseEnabled = _d === void 0 ? true : _d, _e = _a.supportsWebp, supportsWebp = _e === void 0 ? false : _e, flags = _a.flags, publicSession = _a.publicSession, biPageContextData = _a.biPageContextData;
        // the error boundary here is really a last ditch effort - if the error
        // got here the entire page is dead already so we're just saving face
        return (React.createElement(ErrorBoundary, null,
            headTags && (React.createElement(Head, tslib_1.__assign({ isMobile: isMobile, pageThumb: publicSession.seo && publicSession.seo.socialThumbSrc }, headTags))),
            headTags && (React.createElement(CommonHelmet, { isMobile: isMobile, theme: theme, themeSkin: publicSession.themeSkin, bodyClassName: headTags.bodyClassName, flags: flags, language: headTags.language })),
            headTags && ['dev', 'rc'].includes(headTags.environment) && (React.createElement(FlagSwitcher, null)),
            React.createElement(EditorBaseTheme, null),
            React.createElement(BiLoggerContextProvider, { value: biPageContextData },
                React.createElement(SessionContext.Provider, { value: publicSession },
                    React.createElement(BrowserFeatureContext.Provider, { value: { supportsWebp: supportsWebp } },
                        React.createElement(MatureContext.Provider, { value: showMatureContent },
                            React.createElement(MobileContext.Provider, { value: isMobile },
                                React.createElement(ThemeContext.Provider, { value: theme },
                                    React.createElement(ScreenSizeContextProvider, null,
                                        isDuperbrowseEnabled ? (React.createElement(DuperbrowsePageWrapper, null, this.renderPageContent())) : (this.renderPageContent()),
                                        React.createElement(Modals, null, modalsRenderFunc),
                                        backToTop && (React.createElement(BackToTop, { scrollToY: backToTop.scrollToY || 0 })),
                                        this.renderSSRPopups())))))))));
    };
    DaApp.prototype.renderPageContent = function () {
        var _a = this.props, children = _a.children, notificationsRenderFunc = _a.notificationsRenderFunc, notificationsWrapperClassName = _a.notificationsWrapperClassName, disablePageWideNotifications = _a.disablePageWideNotifications;
        return (React.createElement(React.Fragment, null,
            children,
            !disablePageWideNotifications && (React.createElement(Notifications, { wrapperClassName: notificationsWrapperClassName }, notificationsRenderFunc))));
    };
    return DaApp;
}(React.Component));
export default DaApp;
//# sourceMappingURL=DaApp.js.map